import React, { useState, useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import usePullToRefresh from '../hooks/usePullToRefresh';
import RunCard from './RunCard';
import WeekHeader from './WeekHeader';
import HiddenWeeksIndicator from './HiddenWeeksIndicator';
import { useRunPlan } from '../context/RunPlanContext';
import { useSettings } from '../context/SettingsContext';
import { getAbbreviatedDay, formatMileage, formatDateRange } from '../utils/formatters';
import SharePlanModal from './SharePlanModal';

const CalendarView = () => {
  const {
    weeks,
    runs,
    setRuns,
    darkMode,
    t: theme,
    verticalLayout,
    abbreviatedWeekView,
    showRunEmojis,
    c25kViewMode,
    restDayIcon,
    setSelectedDate,
    setShowAddRun,
    setEditingRun,
    setWeekToDuplicate,
    setShowDuplicateMenu,
    addNewWeek,
    addEarlierWeek,
    deleteWeek,
    hideWeek,
    unhideWeek,
    isWeekEmpty,
    hiddenWeeks,
    getStats,
    selectedPlan,
    types
  } = useRunPlan();
  const { autoHideEmptyWeeks } = useSettings();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [shareWeekIndex, setShareWeekIndex] = useState(null);
  const [reorderWeekIndex, setReorderWeekIndex] = useState(null);
  const [deleteWeekIndex, setDeleteWeekIndex] = useState(null);
  const [draggedDay, setDraggedDay] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [touchDragState, setTouchDragState] = useState({
    isDragging: false,
    draggedDay: null,
    currentX: 0,
    currentY: 0,
    offsetX: 0,
    offsetY: 0
  });

  // Haptic feedback helper
  const triggerHaptic = (pattern = 50) => {
    if (navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  };

  // Toggle completion with haptic feedback
  const toggleComplete = (day, run) => {
    const wasCompleted = run.status === 'completed';

    setRuns(prev => {
      const updated = { ...prev };
      updated[day.toDateString()] = {
        ...run,
        status: wasCompleted ? 'planned' : 'completed'
      };
      return updated;
    });

    // Haptic feedback and toast notification
    if (!wasCompleted) {
      triggerHaptic([50, 30, 50]); // Double buzz for completion
      toast.success('Run completed!', {
        icon: '✅',
        duration: 2000,
      });

      // Track run completion in Google Analytics
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'complete_run', {
          run_type: run.type,
          distance: run.distance,
          plan: selectedPlan?.name || 'Custom'
        });
      }
    } else {
      triggerHaptic(30); // Single short buzz for un-complete

      // Track run un-completion in Google Analytics
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'uncomplete_run', {
          run_type: run.type,
          distance: run.distance
        });
      }
    }
  };

  // Pull to refresh handler
  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate a refresh (in a real app, this would fetch new data)
    await new Promise(resolve => setTimeout(resolve, 1000));
    // Force re-render by triggering a state update
    setRuns(prev => ({ ...prev }));
    setIsRefreshing(false);
  };

  // Swap runs between two days
  const swapRuns = (day1, day2) => {
    const day1Key = day1.toDateString();
    const day2Key = day2.toDateString();

    const run1 = runs[day1Key];
    const run2 = runs[day2Key];

    setRuns(prev => {
      const updated = { ...prev };

      if (run1 && run2) {
        // Both days have runs - swap them
        updated[day1Key] = run2;
        updated[day2Key] = run1;
      } else if (run1 && !run2) {
        // Move run1 to day2
        updated[day2Key] = run1;
        delete updated[day1Key];
      } else if (!run1 && run2) {
        // Move run2 to day1
        updated[day1Key] = run2;
        delete updated[day2Key];
      }

      return updated;
    });

    triggerHaptic([30, 20, 30]);
    toast.success('Runs reordered', { duration: 1500 });
  };

  // Handle drag start (desktop)
  const handleDragStart = (day, e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', day.toDateString());
    setDraggedDay(day);
    triggerHaptic(30);
  };

  // Handle drag over (desktop)
  const handleDragOver = (day, e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDay(day);
  };

  // Handle drag enter (desktop)
  const handleDragEnter = (day, e) => {
    e.preventDefault();
    setDragOverDay(day);
  };

  // Handle drag leave (desktop)
  const handleDragLeave = () => {
    setDragOverDay(null);
  };

  // Handle drag end (desktop)
  const handleDragEnd = () => {
    setDraggedDay(null);
    setDragOverDay(null);
  };

  // Handle drop (desktop)
  const handleDrop = (targetDay, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedDay && draggedDay.toDateString() !== targetDay.toDateString()) {
      swapRuns(draggedDay, targetDay);
    }
    setDraggedDay(null);
    setDragOverDay(null);
  };

  // Touch-based drag handlers for mobile
  const handleTouchDragStart = (day, e) => {
    if (!reorderWeekIndex) return;

    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();

    setTouchDragState({
      isDragging: true,
      draggedDay: day,
      currentX: touch.clientX,
      currentY: touch.clientY,
      offsetX: touch.clientX - rect.left,
      offsetY: touch.clientY - rect.top
    });

    triggerHaptic([50, 30, 50]);
  };

  const handleTouchDragMove = (e) => {
    if (!touchDragState.isDragging) return;

    e.preventDefault();
    const touch = e.touches[0];

    setTouchDragState(prev => ({
      ...prev,
      currentX: touch.clientX,
      currentY: touch.clientY
    }));
  };

  const handleTouchDragEnd = (targetDay) => {
    if (!touchDragState.isDragging) return;

    if (touchDragState.draggedDay && targetDay &&
        touchDragState.draggedDay.toDateString() !== targetDay.toDateString()) {
      swapRuns(touchDragState.draggedDay, targetDay);
    }

    setTouchDragState({
      isDragging: false,
      draggedDay: null,
      currentX: 0,
      currentY: 0,
      offsetX: 0,
      offsetY: 0
    });
  };

  const handleTouchCancel = () => {
    setTouchDragState({
      isDragging: false,
      draggedDay: null,
      currentX: 0,
      currentY: 0,
      offsetX: 0,
      offsetY: 0
    });
  };

  const pullToRefreshHandlers = usePullToRefresh(handleRefresh, 80);
  const defaultRunEmojis = {
    easy: '🌱',
    recovery: '❤️‍🩹',
    cross: '🚴',
    workout: '💪',
    race: '🏁',
    long: '⏳'
  };

  const getRunEmoji = (type) => defaultRunEmojis[type] || '🏃';

  // Build list of elements to render (visible weeks + hidden week indicators)
  const elementsToRender = useMemo(() => {
    const elements = [];

    // Determine which weeks should be hidden
    const isWeekHidden = (weekIndex) => {
      if (hiddenWeeks.includes(weekIndex)) return true;
      if (autoHideEmptyWeeks && isWeekEmpty(weekIndex)) return true;
      return false;
    };

    // Build list of visible week indices
    const visibleWeekIndices = weeks
      .map((_, wi) => wi)
      .filter(wi => !isWeekHidden(wi));

    // Build elements list with indicators for hidden week gaps
    visibleWeekIndices.forEach((weekIdx, i) => {
      // Check for hidden weeks between previous visible week and this one
      if (i > 0) {
        const prevWeekIdx = visibleWeekIndices[i - 1];
        const hiddenBetween = [];

        // Find all hidden weeks between previous and current
        for (let j = prevWeekIdx + 1; j < weekIdx; j++) {
          if (isWeekHidden(j)) {
            hiddenBetween.push(j);
          }
        }

        // Add indicator if there are hidden weeks
        if (hiddenBetween.length > 0) {
          elements.push({
            type: 'hidden-indicator',
            key: `hidden-${prevWeekIdx}-${weekIdx}`,
            hiddenWeekIndices: hiddenBetween
          });
        }
      }

      // Add the visible week
      elements.push({
        type: 'week',
        key: `week-${weekIdx}`,
        weekIndex: weekIdx,
        week: weeks[weekIdx]
      });
    });

    return elements;
  }, [weeks, hiddenWeeks, autoHideEmptyWeeks, isWeekEmpty]);

  return (
    <>
      {/* Pull to refresh indicator */}
      <div
        className="fixed top-0 left-0 right-0 flex items-center justify-center z-30 transition-all"
        style={{
          height: `${pullToRefreshHandlers.pullDistance}px`,
          opacity: pullToRefreshHandlers.pullDistance / 80
        }}
      >
        <div className={`flex items-center gap-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
          <RefreshCw
            className={`w-5 h-5 ${isRefreshing || pullToRefreshHandlers.isPulling ? 'animate-spin' : ''}`}
          />
          {pullToRefreshHandlers.isPulling && !isRefreshing && <span className="text-sm">Release to refresh</span>}
          {isRefreshing && <span className="text-sm">Refreshing...</span>}
        </div>
      </div>

      <div className="p-4" {...pullToRefreshHandlers}>
        {/* Add Earlier Week Button */}
        <div className="mb-4">
          <button
            onClick={addEarlierWeek}
            className={'w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 ' +
              (darkMode ? 'bg-slate-800 border-2 border-slate-700 border-dashed hover:bg-slate-700 hover:border-slate-600' :
                'bg-white border-2 border-slate-200 border-dashed hover:bg-slate-50 hover:border-slate-300')}
          >
            <span className={'text-2xl ' + theme.text}>+</span>
            <span>Add Earlier Week</span>
          </button>
        </div>

{elementsToRender.map((element) => {
          if (element.type === 'hidden-indicator') {
            return (
              <HiddenWeeksIndicator
                key={element.key}
                hiddenWeekIndices={element.hiddenWeekIndices}
                weeks={weeks}
                darkMode={darkMode}
                theme={theme}
                onUnhideWeek={unhideWeek}
              />
            );
          }

          // Render week
          const wi = element.weekIndex;
          const wk = element.week;
          const st = getStats(wk);
          const completedMileage = formatMileage(st.total);
          const plannedMileage = formatMileage(st.planned);
          let completed = 0, total = 0;
          wk.forEach(d => {
            const r = runs[d.toDateString()];
            if (r && r.type !== 'cross') {
              total++;
              if (r.status === 'completed') completed++;
            }
          });

          const isEmpty = isWeekEmpty(wi);
          const isHidden = hiddenWeeks.includes(wi);

          return (
            <div key={element.key} className="mb-6">
              <div className={darkMode ? 'bg-slate-800 rounded-2xl shadow-lg border border-slate-700' : 'bg-white rounded-2xl shadow-lg border border-slate-200'}>
                <WeekHeader
                  weekIndex={wi}
                  weekDays={wk}
                  isEmpty={isEmpty}
                  isHidden={isHidden}
                  onHideClick={() => hideWeek(wi)}
                  onUnhideClick={() => unhideWeek(wi)}
                  completedMileage={completedMileage}
                  plannedMileage={plannedMileage}
                  completedRuns={completed}
                  totalRuns={total}
                  darkMode={darkMode}
                  theme={theme}
                  verticalLayout={verticalLayout}
                  onDuplicateClick={() => {
                    setWeekToDuplicate(wi);
                    setShowDuplicateMenu(true);
                  }}
                  onShareClick={() => setShareWeekIndex(wi)}
                  onDeleteClick={() => setDeleteWeekIndex(wi)}
                  isReorderMode={reorderWeekIndex === wi}
                  onToggleReorder={() => {
                    if (reorderWeekIndex === wi) {
                      setReorderWeekIndex(null);
                      setDraggedDay(null);
                    } else {
                      setReorderWeekIndex(wi);
                    }
                  }}
                />

                <div className="p-4">

                {!verticalLayout && (
                  <div className="grid grid-cols-7 gap-3 md:gap-1 mb-3 md:mb-2">
                    {wk.map((day, di) => (
                      <div key={di} className="text-center">
                        <div className={abbreviatedWeekView ? (darkMode ? 'text-sm md:text-xs font-bold text-slate-400' : 'text-sm md:text-xs font-bold text-slate-500') : (darkMode ? 'text-sm md:text-xs font-bold text-slate-400 uppercase' : 'text-sm md:text-xs font-bold text-slate-500 uppercase')}>
                          {abbreviatedWeekView ? getAbbreviatedDay(day) : day.toLocaleDateString('en-US', { weekday: 'short' })}
                        </div>
                        {!abbreviatedWeekView && (
                          <div className={darkMode ? 'text-sm md:text-xs font-semibold text-slate-400' : 'text-sm md:text-xs font-semibold text-slate-600'}>
                            {day.getDate()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className={verticalLayout ? "space-y-2" : "grid grid-cols-7 gap-3 md:gap-2"}>
                  {wk.map((day, di) => {
                    const run = runs[day.toDateString()];
                    const isCompleted = run && run.status === 'completed';

                    // Check if this day is today
                    const today = new Date();
                    const isToday = day.getDate() === today.getDate() &&
                                   day.getMonth() === today.getMonth() &&
                                   day.getFullYear() === today.getFullYear();

                    return (
                      <div
                        key={di}
                        className={verticalLayout ? "" : "flex flex-col items-center w-full min-w-0"}
                        onTouchMove={reorderWeekIndex === wi ? handleTouchDragMove : undefined}
                      >
                        <RunCard
                          day={day}
                          run={run}
                          isCompleted={isCompleted}
                          isToday={isToday}
                          darkMode={darkMode}
                          theme={theme}
                          verticalLayout={verticalLayout}
                          showRunEmojis={showRunEmojis}
                          c25kViewMode={c25kViewMode}
                          getRunEmoji={getRunEmoji}
                          toggleComplete={toggleComplete}
                          triggerHaptic={triggerHaptic}
                          setSelectedDate={setSelectedDate}
                          setShowAddRun={setShowAddRun}
                          setEditingRun={setEditingRun}
                          restDayIcon={restDayIcon}
                          isReorderMode={reorderWeekIndex === wi}
                          isDragging={
                            draggedDay?.toDateString() === day.toDateString() ||
                            touchDragState.draggedDay?.toDateString() === day.toDateString()
                          }
                          isDropTarget={dragOverDay?.toDateString() === day.toDateString()}
                          onDragStart={(e) => handleDragStart(day, e)}
                          onDragOver={(e) => handleDragOver(day, e)}
                          onDragEnter={(e) => handleDragEnter(day, e)}
                          onDragLeave={handleDragLeave}
                          onDragEnd={handleDragEnd}
                          onDrop={(e) => handleDrop(day, e)}
                          onTouchDragStart={(e) => handleTouchDragStart(day, e)}
                          onTouchDragEnd={() => handleTouchDragEnd(day)}
                          onTouchCancel={handleTouchCancel}
                        />
                        {!verticalLayout && showRunEmojis && (
                          <div className="text-xl md:text-lg mt-1 h-7 flex items-center justify-center">
                            {run ? getRunEmoji(run.type) : ''}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="p-4 pb-24 space-y-3">
        <button
          onClick={addNewWeek}
          className={'w-full py-4 rounded-xl font-medium flex items-center justify-center gap-2 ' +
            (darkMode ? 'bg-slate-800 border-2 border-slate-700 border-dashed hover:bg-slate-700 hover:border-slate-600' :
              'bg-white border-2 border-slate-200 border-dashed hover:bg-slate-50 hover:border-slate-300')}
        >
          <span className={'text-2xl ' + theme.text}>+</span>
          <span>Add New Week</span>
        </button>
      </div>

      <SharePlanModal
        isOpen={shareWeekIndex !== null}
        onClose={() => setShareWeekIndex(null)}
        weekDays={shareWeekIndex !== null ? weeks[shareWeekIndex] : []}
        runs={runs}
        selectedPlan={selectedPlan}
        theme={theme}
        darkMode={darkMode}
        getStats={getStats}
        types={types}
      />

      {/* Clear All Runs Confirmation Modal */}
      {deleteWeekIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={() => setDeleteWeekIndex(null)}
        >
          <div
            className={darkMode
              ? 'bg-slate-800 rounded-2xl p-6 max-w-md w-full border border-slate-700'
              : 'bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl'
            }
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4">Clear All Runs?</h3>

            <p className={darkMode ? 'text-slate-300 mb-4' : 'text-slate-700 mb-4'}>
              Are you sure you want to clear all runs from Week {deleteWeekIndex + 1}? This will permanently delete all runs in this week.
            </p>

            <div className={`p-4 rounded-xl mb-4 border-2 ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-300'}`}>
              <div className="text-sm">
                <div className="font-semibold mb-1">
                  {formatDateRange(weeks[deleteWeekIndex][0], weeks[deleteWeekIndex][6])}
                </div>
                <div className={darkMode ? 'text-slate-400' : 'text-slate-600'}>
                  {(() => {
                    const weekRuns = weeks[deleteWeekIndex].filter(day => runs[day.toDateString()]);
                    return weekRuns.length > 0
                      ? `${weekRuns.length} run${weekRuns.length !== 1 ? 's' : ''} will be deleted`
                      : 'No runs in this week';
                  })()}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteWeekIndex(null)}
                className={darkMode
                  ? 'flex-1 px-4 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 transition-colors font-medium'
                  : 'flex-1 px-4 py-3 rounded-xl bg-slate-200 hover:bg-slate-300 transition-colors font-medium'
                }
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteWeek(deleteWeekIndex);
                  setDeleteWeekIndex(null);
                  toast.success('Runs cleared', { icon: '🗑️', duration: 2000 });
                }}
                className={darkMode
                  ? 'flex-1 px-4 py-3 rounded-xl bg-red-900 hover:bg-red-800 text-red-200 transition-colors font-medium'
                  : 'flex-1 px-4 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors font-medium'
                }
              >
                Clear All Runs
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CalendarView;
